import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Loader2, Lock, Trash2, UploadCloud } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

export default function Vault() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => navigate("/"))
      .finally(() => setChecking(false));
  }, [location.state, navigate]);

  const loadDocs = () => {
    axios
      .get(`${API}/vault/documents`, { withCredentials: true })
      .then((r) => setDocs(r.data.documents))
      .catch(() => setDocs([]));
  };

  useEffect(() => {
    if (user) loadDocs();
  }, [user]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("vault.tooBig"));
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (expiresAt) form.append("expires_at", expiresAt);
      await axios.post(`${API}/vault/documents`, form, { withCredentials: true });
      toast.success(t("vault.uploadSuccess"));
      setExpiresAt("");
      loadDocs();
    } catch (err) {
      toast.error(err.response?.status === 400 ? t("vault.badType") : t("vault.uploadError"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const r = await axios.get(`${API}/vault/documents/${doc.id}/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("vault.uploadError"));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(t("vault.deleteConfirm"))) return;
    try {
      await axios.delete(`${API}/vault/documents/${doc.id}`, { withCredentials: true });
      toast.success(t("vault.deleteSuccess"));
      loadDocs();
    } catch (e) {
      toast.error(t("vault.deleteError"));
    }
  };

  if (checking) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-mint animate-spin" strokeWidth={1.5} />
      </main>
    );
  }

  return (
    <main data-testid="vault-page">
      <Header />
      <div className="pt-32 pb-24 max-w-3xl mx-auto px-6 lg:px-10 min-h-screen">
        <Link to="/account" data-testid="vault-back-link" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-mint transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {t("vault.back")}
        </Link>

        <div className="mt-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-mint/10 border border-mint/20 flex items-center justify-center">
            <Lock className="w-6 h-6 text-mint" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl">{t("vault.title")}</h1>
            <p className="mt-1 text-sm text-white/50">{t("vault.subtitle")}</p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="mt-10 rounded-2xl border border-dashed border-mint/30 bg-mint/5 p-8"
          data-testid="vault-upload-zone"
        >
          <h2 className="font-display font-semibold text-white text-lg">{t("vault.uploadTitle")}</h2>
          <p className="mt-1 text-xs text-white/40">{t("vault.uploadHint")}</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className="block text-xs text-white/50 mb-1.5">{t("vault.expiryLabel")}</label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="vault-expiry-input"
                className="w-full rounded-xl border border-white/15 bg-night/70 px-4 py-2.5 text-sm text-white outline-none focus:border-mint/60 [color-scheme:dark]"
              />
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv" onChange={upload} className="hidden" data-testid="vault-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="vault-upload-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <UploadCloud className="w-4 h-4" strokeWidth={2} />}
              {uploading ? t("vault.uploading") : t("vault.uploadCta")}
            </button>
          </div>
        </motion.div>

        <div className="mt-10 space-y-3" data-testid="vault-documents-list">
          {docs === null ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-mint animate-spin" strokeWidth={1.5} /></div>
          ) : docs.length === 0 ? (
            <p data-testid="vault-empty" className="rounded-2xl border border-white/8 bg-card p-10 text-center text-sm text-white/40">
              {t("vault.empty")}
            </p>
          ) : (
            docs.map((doc) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                data-testid={`vault-doc-${doc.id}`}
                className="flex items-center gap-4 rounded-2xl border border-white/8 bg-card px-5 py-4"
              >
                <FileText className="w-5 h-5 text-white/40 shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/85 truncate">{doc.name}</p>
                  <p className="text-[11px] text-white/40 mt-0.5">
                    {fmtSize(doc.size)} · {fmtDate(doc.created_at)}
                    {doc.expires_at && (
                      <span className="text-amber-400/90"> · {t("vault.expiresOn", { date: fmtDate(doc.expires_at) })}</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => download(doc)}
                  data-testid={`vault-download-${doc.id}`}
                  className="p-2 rounded-full text-white/50 hover:text-mint hover:bg-mint/10 transition-colors duration-200"
                  aria-label={t("vault.download")}
                >
                  <Download className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => remove(doc)}
                  data-testid={`vault-delete-${doc.id}`}
                  className="p-2 rounded-full text-white/50 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-200"
                  aria-label={t("vault.delete")}
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </motion.div>
            ))
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
